/**
 * Utility functions for iframe detection and authentication handling
 */

import { initializeConnectSdk, isConnectFullyInitialized, getPersonId, getContactId } from './connectSdk';

/**
 * Check if the current window is running inside an iframe
 */
export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we can't access window.top due to cross-origin restrictions,
    // we're definitely in an iframe
    return true;
  }
};

/**
 * Check if the iframe is hosted within AWS Connect
 */
export const isConnectApp = (): boolean => {
  try {
    const parentUrl = document.referrer || window.top?.location.href;
    return parentUrl ? parentUrl.includes('.my.connect.aws') : false;
  } catch (e) {
    // Fallback: check referrer header
    return document.referrer.includes('.my.connect.aws');
  }
};

/**
 * Open authentication popup and handle the auth flow
 */
export const openAuthPopup = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const popup = window.open(
      '/api/auth/login?popup=true',
      'auth-popup',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    if (!popup) {
      reject(new Error('Failed to open popup - popup blocker may be active'));
      return;
    }

    let authSucceeded = false;

    // Listen for successful authentication
    const messageListener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data === 'auth-success') {
        authSucceeded = true;
        cleanup();
        popup.close();
        resolve();
      }
    };

    // Check popup URL and status
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        cleanup();
        if (authSucceeded) {
          resolve();
        } else {
          reject(new Error('Authentication popup closed without completing'));
        }
        return;
      }
      
      try {
        // If popup navigated back to main app, auth succeeded
        if (popup.location.pathname === '/' || popup.location.pathname.includes('/chat')) {
          authSucceeded = true;
          cleanup();
          popup.close();
          resolve();
        }
      } catch (e) {
        // Cross-origin error is expected during auth flow
      }
    }, 1000);

    // Cleanup function
    const cleanup = () => {
      clearInterval(checkClosed);
      clearTimeout(timeout);
      window.removeEventListener('message', messageListener);
    };

    // Timeout after 5 minutes
    const timeout = setTimeout(() => {
      cleanup();
      if (!popup.closed) {
        popup.close();
      }
      reject(new Error('Authentication timeout'));
    }, 300000);

    window.addEventListener('message', messageListener);
  });
};

/**
 * Initialize Connect SDK if running in Connect environment
 * Waits for full initialization including contact attributes
 */
export const initializeConnectIfNeeded = async (): Promise<void> => {
  if (!isInIframe() || !isConnectApp()) {
    return;
  }

  try {
    console.log('Initializing Connect SDK...');
    await initializeConnectSdk();
    console.log('Connect SDK fully initialized with contact data');
  } catch (error) {
    console.warn('Failed to fully initialize Connect SDK:', error);
    throw error;
  }
};